import React, { FC, useMemo, useState } from 'react';
import { FolderManagerBreadcrumb, ProjectStepAction } from '../../../../interfaces';

import { NgBreadcrumbs, NgCard, NgCardContent, NgCardHeader } from '../../../UI';
import { WorkspaceTemplateQueriesForm } from './components/WorkspaceTemplateQueriesForm';
import { WorkspaceTemplateQueriesList } from './components/WorkspaceTemplateQueriesList';
import {
  useCreateProjectTemplateQueryMutation,
  useDeleteProjectTemplateQueryMutation,
  useGetProjectTemplateQueriesQuery,
  useUpdateProjectTemplateQueryMutation,
} from '../../../../services';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../../../store';
import { CircularLoader } from '../../../../shared';
import { projectPagesMap } from '../../../../constants';

interface Props {
  workspaceData: ProjectTemplate;
  isLoading?: boolean;
}

export const QueryEditPage: FC<Props> = ({ workspaceData, isLoading: isParentLoading }) => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const [editedQuery, setEditedQuery] = useState<WorkspaceQuery | null>(null);

  const { data: queriesList, isLoading: isQueriesListLoading } = useGetProjectTemplateQueriesQuery(
    {
      projectTemplateId: +projectId!,
    },
    { skip: !projectId },
  );

  const [createQuery, { isLoading: isCreateQueryLoading }] = useCreateProjectTemplateQueryMutation();
  const [updateQuery, { isLoading: isUpdateQueryLoading }] = useUpdateProjectTemplateQueryMutation();
  const [deleteQuery, { isLoading: isDeleteQueryLoading }] = useDeleteProjectTemplateQueryMutation();

  const isLoading =
    isQueriesListLoading || isParentLoading || isUpdateQueryLoading || isDeleteQueryLoading || isCreateQueryLoading;

  const breadcrumbs = useMemo(() => {
    const initial = [
      {
        id: 0,
        name: projectPagesMap.QUERIES.displayName,
      },
    ];

    if (editedQuery) {
      initial.push({
        id: editedQuery.ID,
        name: 'Edit',
      });
    }

    return initial;
  }, [editedQuery]);

  const handleBreadcrumbClick = (data: { index: number; item: FolderManagerBreadcrumb }) => {
    //@todo guard when changes are not saved
    if (data.index === 0) {
      setEditedQuery(null);
    }
  };

  const handleAction = async (action: NgAction<WorkspaceQuery | { display_name: string; query: string } | null>) => {
    switch (action.type) {
      case 'CREATE':
        if (!projectId) {
          dispatch(
            addNotification({
              type: 'error',
              message: 'Couldn\'t complete this action — no workspace was found.',
            }),
          );
          return;
        }

        try {
          const displayName = 'New Workspace Template Query';

          await createQuery({
            projectTemplateId: +projectId,
            data: { display_name: displayName },
          }).unwrap();

          dispatch(
            addNotification({
              type: 'success',
              message: `Query "${displayName}" has been successfully created.`,
            }),
          );
        } catch (error: any) {
          const createErrorMessage = 'Couldn\'t create the query. Please try again.';
          dispatch(
            addNotification({
              type: 'error',
              message: createErrorMessage,
            }),
          );
          console.error(createErrorMessage);
        }
        break;
      case 'UPDATE':
        if (!projectId) {
          dispatch(
            addNotification({
              type: 'error',
              message: 'Couldn\'t complete this action — no workspace was found.',
            }),
          );
          return;
        }

        if (!editedQuery?.ID) {
          dispatch(
            addNotification({
              type: 'error',
              message: 'Couldn\'t complete this action — no workspace was found.',
            }),
          );
          return;
        }

        try {
          const response = await updateQuery({
            projectTemplateId: +projectId,
            queryId: editedQuery.ID,
            data: action.payload,
          }).unwrap();

          setEditedQuery(response);

          dispatch(
            addNotification({
              type: 'success',
              message: `Query "${editedQuery.DisplayName}" has been successfully updated.`,
            }),
          );
        } catch (error: any) {
          const updateErrorMessage = 'Couldn\'t update the query. Please try again.';
          dispatch(
            addNotification({
              type: 'error',
              message: updateErrorMessage,
            }),
          );
          console.error(updateErrorMessage);
        }
        break;
      case 'DELETE':
        if (!projectId) {
          dispatch(
            addNotification({
              type: 'error',
              message: 'Couldn\'t delete the query — no workspace was found.',
            }),
          );
          return;
        }

        if (!(action.payload && 'ID' in action.payload)) {
          dispatch(
            addNotification({
              type: 'error',
              message: 'Couldn\'t delete the query — query data is missing.',
            }),
          );
          return;
        }

        await deleteQuery({
          projectTemplateId: +projectId,
          queryId: action.payload.ID,
        });
        break;
      case 'EDIT':
        setEditedQuery(action.payload as WorkspaceQuery);
        break;
      case 'CANCEL_EDIT':
        setEditedQuery(null);
        break;
      default:
        console.error('Unsupported action type!');
    }
  };

  return (
    <NgCard>
      <NgCardContent>
        <NgCardHeader hideFilters>
          <NgBreadcrumbs data={breadcrumbs} onClick={handleBreadcrumbClick} />
        </NgCardHeader>

        <br />

        {editedQuery ? (
          <WorkspaceTemplateQueriesForm
            workspaceData={workspaceData}
            query={editedQuery}
            onAction={handleAction}
            isLoading={isLoading}
          />
        ) : (
          <WorkspaceTemplateQueriesList items={queriesList || []} isLoading={isLoading} onAction={handleAction} />
        )}
      </NgCardContent>
    </NgCard>
  );
};
